import { isStringWithValue } from '@/utils/helpers/isStringWithValue';
import { IImageLeftRight } from '@/utils/interfaces/bloks';
import { CTA } from '../Shared/CTA/CallToAction';
import { FormattedText } from '../Shared/FormattedText';
import { Icon } from '../Shared/Icon';
import { Left } from '../Shared/Icons/BrandBackgrounds/ImageLeftRight/Left';
import { Right } from '../Shared/Icons/BrandBackgrounds/ImageLeftRight/Right';
import { SmallEmblem } from '../Shared/Icons/BrandBackgrounds/ImageLeftRight/SmallEmblem';
import { Container } from '../Shared/Layout/Container';
import { SbSection } from '../Shared/Layout/SbSection';
import { SbImage } from '../Shared/Image/SbImage';
import { SbImageWrapper } from '../Shared/Image/SbImageWrapper';
import { isArrayWithValue } from '@/utils/helpers/isArrayWithValue';
import { WysiwygContainer } from '../Shared/Layout/WysiwygContainer';
import { render } from 'storyblok-rich-text-react-renderer';
import { GreenEmblem } from '../Shared/Icons/BrandBackgrounds/ImageLeftRight/GreenEmblem';

interface IProps {
	blok: IImageLeftRight & { video_url?: string }; 
	preview: boolean;
  }
  

export const ImageLeftRight: React.FC<IProps> = ({ blok, preview }) => {
	let largeEmblem = null;
	let smallEmblem = null;

	const containerClasses = ['relative', 'py-24'];
	const flexControlClasses = ['relative', 'flex', 'flex-col-reverse', 'w-full', 'lg:flex-row'];
	const textContainerClasses = ['justify-center', 'flex-1', 'md:flex', 'md:flex-col'];
	const imageContainerClasses = [
		'justify-center',
		'relative',
		'md:flex',
		'md:flex-col',
	];

	const isLargeGreenSection = blok.emblem === 'large_green';

	if (isLargeGreenSection) {
		containerClasses.push('min-h-[544px]', 'lg:py-24', 'flex', 'items-center');
		imageContainerClasses.push('lg:min-h-[288px]', 'lg:min-w-[444px]');
	}

	if (blok.bg_colour === 'dark-green') {
		containerClasses.push('dark', 'bg-green-dark');
	} else if (blok.bg_colour === 'white') {
		containerClasses.push('bg-white');
	} else if (blok.bg_colour === 'grey') {
		containerClasses.push('bg-gray-light');
	}

	if (blok.layout === 'image-left') {
		flexControlClasses.push('lg:flex-row-reverse');
		textContainerClasses.push('lg:mr-6', 'xl:ml-20', 'xl:mr-5');
	} else {
		textContainerClasses.push('lg:mr-20', 'xl:ml-5');
	}

	if (blok.emblem === 'large' || blok.emblem === 'large_green') {
		if (blok.layout === 'image-left') {
			largeEmblem = isLargeGreenSection ? (
				<Icon
					className="absolute md:block hidden top-0 left-0 w-[324px] h-full sm:w-[474px]  lg:w-[553px] lg:left-[0%] xl:w-[703px]"
					icon={<GreenEmblem />}
				/>
			) : (
				<Icon
					className="absolute top-0 left-6 w-[324px] h-[336px] sm:w-[474px] sm:h-[486px] lg:h-[585px] lg:w-[553px] lg:left-[2%] xl:h-[735px] xl:w-[703px] xl:left-[5.5%]"
					icon={<Left />}
				/>
			);
		} else {
			largeEmblem = (
				<Icon
					className="absolute top-0 right-6 w-[324px] h-[336px] sm:w-[474px] sm:h-[486px] lg:h-[585px] lg:w-[553px] lg:right-[2%] xl:h-[735px] xl:w-[703px] xl:right-[5.5%]"
					icon={<Right />}
				/>
			);
		}
	} else {
		const smallEmblemClasses = [
			'h-[295px]',
			'w-[169px]',
			'md:h-[422px]',
			'md:w-[240px]',
			'lg:w-[318px]',
			'lg:h-[555px]',
			'xl:w-[368px]',
			'xl:h-[644px]',
			'absolute-center',
		];

		if (blok.layout === 'image-left') {
			smallEmblemClasses.push('rotate-180');
		}

		smallEmblem = (
			<Icon
				className={smallEmblemClasses.join(' ')}
				icon={<SmallEmblem className="w-full" />}
			/>
		);
	}

	return (
		<SbSection blok={blok} preview={preview} classNames={containerClasses.join(' ')}>
			{largeEmblem && <>{largeEmblem}</>}

			<Container type="wide">
				<div className={flexControlClasses.join(' ')}>
					<div className={textContainerClasses.join(' ')}>
						<span className={`text-sm font-bold ${isLargeGreenSection ? 'text-green-dark text-3xl lg:text-left text-center block md:text-6xl lg:mt-0 md:mt-[90px]' : 'text-green'}`}>
							{blok.sub_heading}
						</span>

						<FormattedText
							tag="h3"
							text={blok.heading}
							className={`mt-2 dark:text-white ${isLargeGreenSection ? 'text-green text-3xl md:text-6xl lg:text-left text-center block leading-[48px] font-normal mb-6' : ''
								}`}
						/>

						{blok.wysiwig_description?.length ? (
							blok.wysiwig_description.map((nested, index) => (
								<WysiwygContainer
									classname="lg:!my-0 govt-wysiwig"
									key={nested._uid || index}
									narrow={true}
								>
									<div className="wyswiyg-content">
										{nested.content ? render(nested.content) : null}
									</div>
								</WysiwygContainer>
							))
						) : (
							<span className="block mt-6 text-base opacity-80 dark:text-white">
								{blok.description}
							</span>
						)}

						<div className="mt-4">
							{blok.ctas.map((cta, key) => {
								return <CTA blok={cta} key={key} />;
							})}
						</div>
					</div>

					<div className={imageContainerClasses.join(' ')}>
						<div className="max-w-[290px] mx-auto sm:max-w-[790px] lg:py-0">
						{blok.video_url ? (
				<div className="relative z-20">
					<div
					style={{
						width: '790px',
						height: '444px',
						borderRadius: '12px',
						overflow: 'hidden',
						boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
						margin: '0 auto',
					}}
					>
					<iframe
						src={`https://player.vimeo.com/video/${blok.video_url.split('/').pop()}?autoplay=1&muted=1&background=1&autopause=0&loop=1`}
						width="790"
						height="444"
						frameBorder="0"
						allow="autoplay; fullscreen; picture-in-picture"
						allowFullScreen
						style={{ width: '100%', height: '100%', display: 'block' }}
						title="Vimeo Video"
					/>
					</div>
				</div>
				) : (
				blok.image && isStringWithValue(blok.image.filename) && (
					<div className="relative z-20">
					<SbImageWrapper shadow={false} rounded="lg">
						<SbImage
						src={blok.image.filename}
						alt={blok.image.alt}
						disablePngBg={true}
						layout="intrinsic"
						objectFit="cover"
						/>
					</SbImageWrapper>
					</div>
				)
				)}

							{smallEmblem && <>{smallEmblem}</>}
						</div>
					</div>
				</div>
			</Container>
		</SbSection>
	);
};
